import settings from '@config/settings'
import { BANKY_URLS } from '@constants'
import cn from '@integrations/classnames'
import type { FormHTMLAttributes, HTMLAttributes } from '@integrations/react'
import { tr } from 'mmfintech-commons'
import LanguageMenu from '../LanguageMenu'
import styles from './AuthShell.module.scss'

type Props = HTMLAttributes<HTMLDivElement>
type AuthShellProps = Props & {
  layout?: 'centered' | 'split'
}
type FormProps = FormHTMLAttributes<HTMLFormElement>
type AuthTitleProps = Props & {
  variant?: 'default' | 'compact' | 'display'
}
type AuthPromptProps = HTMLAttributes<HTMLElement> & {
  as?: 'div' | 'p'
}
type AuthLanguageMenuProps = {
  bordered?: boolean
  placement?: 'floating' | 'inline'
}

export function AuthShell({ className, children, layout = 'centered', ...props }: AuthShellProps) {
  return (
    <div
      className={cn(styles['auth-shell'], 'auth-shell', className, {
        [styles['auth-shell-split']]: layout === 'split',
        'auth-shell-split': layout === 'split'
      })}
      {...props}
    >
      {children}
    </div>
  )
}

export function AuthPane({ className, children, ...props }: Props) {
  return (
    <div className={cn(styles['auth-pane'], 'auth-pane', className)} {...props}>
      {children}
    </div>
  )
}

export function AuthPaneHeader({ className, children, ...props }: Props) {
  return (
    <div className={cn(styles['auth-pane-header'], 'auth-pane-header', className)} {...props}>
      {children}
    </div>
  )
}

export function AuthPaneBody({ className, children, ...props }: Props) {
  return (
    <div className={cn(styles['auth-pane-body'], 'auth-pane-body', className)} {...props}>
      {children}
    </div>
  )
}

export function AuthFooter({ className, ...props }: Props) {
  return (
    <div className={cn(styles['auth-footer'], 'auth-footer', className)} {...props}>
      <span>{`${new Date().getFullYear()} © banky`}</span>
      <a href={BANKY_URLS.TERMS_AND_CONDITIONS} target='_blank' rel='noreferrer' data-test='footer-terms'>
        {tr('FRONTEND.AUTH.FOOTER.TERMS', 'Terms and Conditions')}
      </a>
      <a href={BANKY_URLS.PRIVACY_POLICY} target='_blank' rel='noreferrer' data-test='footer-privacy'>
        {tr('FRONTEND.AUTH.FOOTER.PRIVACY', 'Privacy Policy')}
      </a>
      <a href={settings.customerServiceUrl} data-test='footer-support'>
        {tr('FRONTEND.AUTH.FOOTER.SUPPORT', 'Merchant Support')}
      </a>
    </div>
  )
}

export function AuthContainer({ className, children, ...props }: Props) {
  return (
    <div className={cn(styles['auth-container'], 'auth-container', className)} {...props}>
      {children}
    </div>
  )
}

export function AuthSurface({ className, children, ...props }: Props) {
  return (
    <div
      className={cn(styles['auth-surface'], 'auth-surface', 'raised-frame-surface', 'shadow-card', className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function AuthTitle({ className, children, variant = 'default', ...props }: AuthTitleProps) {
  return (
    <div
      className={cn(styles['auth-title'], 'auth-title', className, {
        [styles['auth-title-compact']]: variant === 'compact',
        'auth-title-compact': variant === 'compact',
        [styles['auth-title-display']]: variant === 'display',
        'auth-title-display': variant === 'display'
      })}
      {...props}
    >
      {children}
    </div>
  )
}

export function AuthSubtitle({ className, children, ...props }: Props) {
  return (
    <div className={cn(styles['auth-subtitle'], 'auth-subtitle', className)} {...props}>
      {children}
    </div>
  )
}

export function AuthForm({ className, children, ...props }: FormProps) {
  return (
    <form className={cn(styles['auth-form'], 'auth-form', className)} {...props}>
      {children}
    </form>
  )
}

export function AuthPrompt({ as: Component = 'div', className, children, ...props }: AuthPromptProps) {
  return (
    <Component className={cn(styles['auth-prompt'], 'auth-prompt', className)} {...props}>
      {children}
    </Component>
  )
}

export function AuthLanguageMenu({ bordered = false, placement = 'floating' }: AuthLanguageMenuProps) {
  return (
    <div
      className={cn(styles['auth-language-menu'], 'language-select-wrapper', {
        [styles['auth-language-menu-inline']]: placement === 'inline',
        'auth-language-menu-inline': placement === 'inline'
      })}
    >
      <LanguageMenu bordered={bordered} />
    </div>
  )
}
